import {marked} from 'marked';

/**
 * Model output is Markdown. Rendering it as HTML is an injection surface — the
 * text is model-generated and, worse, quotes tool results containing catalog
 * data somebody else wrote.
 *
 * Two independent layers:
 *
 *  1. **This renderer.** marked v4 removed its `sanitize` option and passes raw
 *     HTML straight through, so the renderer below neutralises it explicitly:
 *     HTML in the source is escaped to visible text, and link hrefs are limited
 *     to http/https/mailto. Showing `<script>` as literal text is also the more
 *     honest rendering — the operator sees what the model actually wrote.
 *  2. **Angular.** The result is bound with plain `[innerHTML]`, so Angular's
 *     sanitizer runs over it as well. Nothing here calls
 *     `bypassSecurityTrustHtml` — that is what would switch the layer off. Do
 *     not add it.
 */

const renderer = new marked.Renderer();

// Block-level and inline raw HTML: render as text instead of markup.
renderer.html = (html: string) => escapeHtml(html);

const SAFE_PROTOCOL = /^(https?:|mailto:|#|\/)/i;

renderer.link = (href: string, title: string, text: string) => {
  if (!href || !SAFE_PROTOCOL.test(href.trim())) {
    // javascript:, data:, vbscript: — keep the label, drop the navigation.
    return text;
  }
  const attrs = title ? ` title="${escapeHtml(title)}"` : '';
  return `<a href="${escapeHtml(href)}"${attrs} target="_blank" rel="noopener noreferrer">${text}</a>`;
};

renderer.image = (href: string, title: string, text: string) =>
  // Remote images in an admin panel are a tracking and layout risk, and nothing
  // the agent produces needs them.
  escapeHtml(text || href || '');

marked.setOptions({
  gfm: true,
  breaks: true,
  // Anchors would be dead links inside a floating panel, and the id attributes
  // could collide with the host page.
  headerIds: false,
  mangle: false,
  renderer,
});

export function renderMarkdown(source: string): string {
  if (!source) {
    return '';
  }
  try {
    return marked.parse(source) as string;
  } catch (e) {
    // A half-streamed document can be malformed mid-render. Falling back to the
    // escaped source keeps the text visible instead of blanking the bubble.
    return escapeHtml(source);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
